import { useCallback, useEffect, useMemo, useState } from 'react';
import { captureCatch, reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import {
  applyCheckCommand,
  CHECK_HISTORY_STATUSES,
  listChecksForBranch,
  type CheckCommandOperation,
  type CheckRow,
  type CheckStatus,
} from '../../infra/checksRepo';

export type ChecksPortfolioFilter = 'portfolio' | 'history';

const STATUS_BY_FILTER: Record<ChecksPortfolioFilter, CheckStatus[]> = {
  portfolio: ['in_portfolio'],
  history: CHECK_HISTORY_STATUSES,
};

/** Tope de carga de la cartera — el servidor trae los N más próximos a vencer. */
export const CHECKS_PORTFOLIO_FETCH_LIMIT = 200;

type Params = {
  branchId: string | null;
  enabled: boolean;
};

export function useChecksPortfolio({ branchId, enabled }: Params) {
  const [items, setItems] = useState<CheckRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<ChecksPortfolioFilter>('portfolio');
  // true cuando el servidor devolvió el tope: la búsqueda local no cubre todo.
  const [hitLimit, setHitLimit] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const reload = useCallback(async () => {
    if (!branchId || !enabled) {
      setItems([]);
      setHitLimit(false);
      setLoadError(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await listChecksForBranch({
        branchId,
        statuses: STATUS_BY_FILTER[filter],
        limit: CHECKS_PORTFOLIO_FETCH_LIMIT + 1,
      });
      const capped = rows.length > CHECKS_PORTFOLIO_FETCH_LIMIT;
      setItems(capped ? rows.slice(0, CHECKS_PORTFOLIO_FETCH_LIMIT) : rows);
      setHitLimit(capped);
      setLoadError(false);
    } catch (err) {
      captureCatch('cash', 'checks.list')(err);
      // Conservar el snapshot; la sección muestra error + retry.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [branchId, enabled, filter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const portfolioTotal = useMemo(
    () => (filter === 'portfolio' ? items.reduce((acc, c) => acc + c.amount, 0) : 0),
    [filter, items],
  );

  const submit = useCallback(
    async (params: {
      operation: CheckCommandOperation;
      checkId: string;
      settlementId?: string;
      expenseId?: string;
      reason?: string;
      note?: string;
      successMessage: string;
    }) => {
      if (submitting) return false;
      setSubmitting(true);
      try {
        await applyCheckCommand({
          operation: params.operation,
          checkId: params.checkId,
          settlementId: params.settlementId,
          expenseId: params.expenseId,
          reason: params.reason,
          note: params.note,
          operationId: `check-${params.operation}-${params.checkId}-${Date.now()}`,
        });
        showSuccess(params.successMessage);
        await reload();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        reportUserVisibleFailure({ module: 'cash', op: `checks.${params.operation}`, error: err });
        showError(message);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, reload],
  );

  return {
    items,
    loading,
    submitting,
    filter,
    setFilter,
    portfolioTotal,
    hitLimit,
    loadError,
    fetchLimit: CHECKS_PORTFOLIO_FETCH_LIMIT,
    reload,
    submit,
  };
}

export type ChecksPortfolioFlow = ReturnType<typeof useChecksPortfolio>;
